import { CONFIG, STEP } from "./config";
import type { EchoEngine } from "./engine";
import type { EngineEvent } from "./events";

/**
 * RunDriver — owns the display-frame accumulator (spec §19.1).
 * - Clamps a single display delta to 0.10 s.
 * - Processes at most 12 mechanics steps per frame; excess debt is dropped
 *   (pause-and-recover) rather than fast-forwarded through unseen collisions.
 * - The 0.075 s hit freeze runs on real time and consumes no sim steps,
 *   preserving player-route/echo alignment (spec §4.5).
 */
export class RunDriver {
  private acc = 0;
  freeze = 0;

  constructor(readonly engine: EchoEngine) {}

  /** Advance with wall-clock delta seconds; returns engine events. */
  update(dtWall: number): EngineEvent[] {
    if (this.engine.over) return [];
    if (this.freeze > 0) {
      this.freeze -= dtWall;
      return [];
    }
    const dt = Math.min(dtWall, 0.1);
    this.acc += dt;
    const events: EngineEvent[] = [];
    let steps = 0;
    while (this.acc >= STEP && steps < 12 && !this.engine.over) {
      this.acc -= STEP;
      steps++;
      const stepEvents = this.engine.step();
      for (const e of stepEvents) {
        events.push(e);
        if (e.type === "hit") this.freeze = CONFIG.hitFreezeSeconds;
      }
      if (this.freeze > 0) break; // freeze begins immediately on the hit step
    }
    if (this.acc >= STEP) this.acc = this.acc % STEP; // drop debt, never fast-forward
    return events;
  }
}
